import { create } from 'zustand';

/**
 * uiStore — cross-component UI chrome preferences.
 *
 * `sidebarCollapsed` is the user's manual rail preference on large screens
 * (lg+). It lets the reader shrink the 248px labelled rail down to the 76px
 * icon-only rail to hand the extra width to the central canvas. It is a
 * *preference* layered on top of the responsive breakpoints, not a replacement:
 *  - `< md`   : sidebar is hidden regardless (mobile bottom tab bar).
 *  - `md–lg`  : rail is forced to 76px icon-only regardless (no room for labels).
 *  - `lg+`    : rail follows this flag — 248px when expanded, 76px when collapsed.
 *
 * Persisted to localStorage so the choice survives reloads.
 */

const STORAGE_KEY = 'cloudmaster_sidebar_collapsed';

interface UIState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (value: boolean) => void;
}

function getInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persist(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* storage unavailable (private mode / quota) — preference stays in-memory */
  }
}

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: getInitialCollapsed(),

  toggleSidebar: () =>
    set((state) => {
      const next = !state.sidebarCollapsed;
      persist(next);
      return { sidebarCollapsed: next };
    }),

  setSidebarCollapsed: (value: boolean) => {
    persist(value);
    set({ sidebarCollapsed: value });
  },
}));
